import React, { useEffect, useRef, useState } from "react";
import Loader from "../../../components/Loader";
import styles from "./articles.module.css";
import { useAuth0 } from "@auth0/auth0-react";
import { Helmet } from "react-helmet";
import { Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton, Slide, Snackbar, TablePagination, Typography } from "@mui/material";
import { Home, X } from "react-feather";
import Footer from "../../../components/Footer";
import ArticleFilter from "./components/ArticleFilter";
import ArticleCard from "./components/ArticleCard";
import axios from "axios";
import { TableLoader } from "../../../components/LoaderC";
import { toast } from 'react-smart-toaster';
import { AddLogCallAPI } from "../../../components/AddLogs";
import withCustomAuthenticationRequired from "../../../auth/withCustomAuthenticationRequired";

const ArticleList = ({ history, match }) => {
    const { getAccessTokenSilently } = useAuth0();
    const [loading, setLoading] = useState(false);
    const [contentLoading, setContentLoading] = useState(true);
    const [articleList, setArticleList] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [loadError, setLoadError] = useState(false);
    const [searchKey, setSearchKey] = useState("");
    const [reloadKey, setReloadKey] = useState(0);
    const [page, setPage] = useState(0);
    const [perPage, setPerPage] = useState(8);
    const [filterData, setFilterData] = useState({ searchKey: "", property: "", category: "" });
    const [showEditPopup, setShowEditPopup] = useState(false);
    const [showDeletePopup, setShowDeletePopup] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMsg, setSnackbarMsg] = useState("");
    const [selectecId, setSelectecId] = useState(0);
    const [isPublished, setIsPublished] = useState(true);
    const latestRequest = useRef(0);

    // Debounce search so we don't hit the API on every keystroke
    useEffect(() => {
        const timer = setTimeout(() => {
            setSearchKey(filterData.searchKey);
            setPage(0);
        }, filterData.searchKey ? 400 : 0);
        return () => clearTimeout(timer);
    }, [filterData.searchKey]);

    useEffect(() => {
        const requestId = ++latestRequest.current;

        async function getArticles() {
            try {
                const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });

                const response = await axios.get(process.env.REACT_APP_RESOURCE_API_URL + "v1/articles/paginated", {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                    params: {
                        page: page + 1,
                        per_page: perPage,
                        search: searchKey || undefined,
                        property_id: filterData.property !== "" ? filterData.property : undefined,
                        category_id: filterData.category !== "" ? filterData.category : undefined,
                    },
                });

                // Ignore responses from requests that were superseded by newer filters/pages
                if (requestId !== latestRequest.current) return;

                const rows = response.data?.data || [];
                const total = response.data?.pagination?.total || 0;

                // The last item on a page was deleted, so step back to the previous page
                if (rows.length === 0 && page > 0 && total > 0) {
                    setPage(Math.max(Math.ceil(total / perPage) - 1, 0));
                    return;
                }

                setArticleList(rows);
                setTotalCount(total);
                setLoadError(false);
            } catch (error) {
                if (requestId !== latestRequest.current) return;
                console.error('[KnowledgeBase] Failed to load article list', error);
                setLoadError(true);
                toast.error(error?.response?.data?.message || 'Unable to load articles. Please try again.');
            } finally {
                if (requestId === latestRequest.current)
                    setContentLoading(false);
            }
        }
        getArticles();
    }, [getAccessTokenSilently, page, perPage, searchKey, filterData.property, filterData.category, reloadKey]);

    const onUpdate = (data) => {
        setArticleList(prev => {
            let updateList = prev?.map(item => {
                if (item?.id === data?.id)
                    return data;
                else
                    return item;
            })
            return updateList;
        });
    }

    const confirmStChange = async () => {
        let data = {};
        if (isPublished) {
            data = { is_published: 1 };
        } else {
            data = { is_published: 0 };
        }
        setShowEditPopup(false);
        setLoading(true);
        const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });

        axios.put(process.env.REACT_APP_RESOURCE_API_URL + 'v1/articles/' + selectecId, data, {
            headers: {
                Authorization: `Bearer ${token}`,
            }
        }).then(response => {
            setSnackbarOpen(true);
            setSnackbarMsg(response.data.message);
            setLoading(false);
            setSelectecId(0);

            onUpdate(response.data.data);

            let logData = {
                'title': isPublished ? 'Article has been published' : 'Article has been drafted',
                'description': [
                    'Title: ' + response.data.data?.title,
                    'Categories: ' + response.data.data?.category_names.join(', '),
                    'Properties: ' + response.data.data?.property_names.join(', ')
                ]
            }

            AddLogCallAPI(logData, token);
        }).catch(error => {
            if (typeof error.response !== 'undefined')
                toast.error(error.response.data.message);
            setLoading(false);
        });
    }

    const confirmDelete = async () => {
        setShowDeletePopup(false);
        setLoading(true);
        const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });

        axios.delete(process.env.REACT_APP_RESOURCE_API_URL + 'v1/articles/' + selectecId, {
            headers: {
                Authorization: `Bearer ${token}`,
            }
        }).then(response => {
            setSnackbarOpen(true);
            setSnackbarMsg(response.data.message);
            setLoading(false);

            // Reload from the server so the page refills and the total stays accurate
            setReloadKey(prev => prev + 1);
            setSelectecId(0);

            let logData = {
                'title': 'An article has been deleted',
                'description': [
                    'Title: ' + response.data.data?.title,
                    'Categories: ' + response.data.data?.category_names.join(', '),
                    'Properties: ' + response.data.data?.property_names.join(', ')
                ]
            }

            AddLogCallAPI(logData, token);
        }).catch(error => {
            if (typeof error.response !== 'undefined')
                toast.error(error.response.data.message);
            setLoading(false);
        });
    }

    return (<React.Fragment>
        {loading && <Loader />}

        <Helmet title="Knowledge Base" />
        <div className={`${styles.pageTitleWrap}`}>
            <Typography display="inline" className={`${styles.pageTitle}`}>Knowledge Base</Typography>
            <nav aria-label="breadcrumb">
                <ol className={`${styles.breadcrumb}`}>
                    <li className={`${styles.breadcrumbItem}`}><span><Home /></span>Resources</li>
                    <li className={`${styles.breadcrumbItem} ${styles.active}`}>Knowledge Base</li>
                </ol>
            </nav>
        </div>
        <div className={`${styles.BodyCon}`}>
            <div className={`${styles.mainCardDiv}`}>
                <div className={`${styles.mainCard}`}>
                    <div className="control-pane">
                        {contentLoading && (<div className="Loader TableLoader"><TableLoader /></div>)}

                        {!contentLoading && (<div className={`${styles.Row}`}>
                            <ArticleFilter filterData={filterData} setFilterData={setFilterData.bind(this)} setPage={setPage.bind(this)} />
                            <div className={`${styles.NotesRow}`}>

                                {articleList.map((item) => {
                                    return (<ArticleCard key={item?.id} item={item} setShowDeletePopup={setShowDeletePopup.bind(this)} setShowEditPopup={setShowEditPopup.bind(this)} setIsPublished={setIsPublished.bind(this)} setSelectecId={setSelectecId.bind(this)} />);
                                })}

                                {articleList.length > 0 && (<div className="Pagination TablePaginationNew List">
                                    <TablePagination
                                        count={totalCount}
                                        color="primary"
                                        page={page}
                                        rowsPerPage={perPage}
                                        onPageChange={(e, v) => setPage(v)}
                                        rowsPerPageOptions={[
                                            { label: '4', value: 4 },
                                            { label: '8', value: 8 },
                                            { label: '16', value: 16 },
                                            { label: '50', value: 50 },
                                        ]}
                                        onRowsPerPageChange={(e) => {
                                            setPage(0);
                                            setPerPage(e.target.value);
                                        }}
                                        labelRowsPerPage={'Alert per Page:'}
                                    />
                                </div>)}

                                {!contentLoading && articleList.length === 0 && loadError && (<div className={`${styles.NoDataMain}`}>
                                    <p className={`${styles.NoDataText}`}>Something went wrong</p>
                                    <p className={`${styles.NoDataTextSub}`}>We couldn't load articles. Please refresh the page to try again.</p>
                                </div>)}

                                {!contentLoading && articleList.length === 0 && !loadError && (<div className={`${styles.NoDataMain}`}>
                                    <div className={`${styles.NoDataIMG}`}>
                                        <img src="/static/img/article-kb.png" alt="No Data" />
                                    </div>
                                    <p className={`${styles.NoDataText}`}>Oops!</p>
                                    <p className={`${styles.NoDataTextSub}`}>The articles don't seem to exist.</p>
                                </div>)}

                            </div>
                        </div>)}
                    </div>
                </div>
            </div>
        </div>

        <Dialog
            open={showEditPopup}
            onClose={(e) => setShowEditPopup(false)}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
            className="NewPopUp"
            fullWidth
            maxWidth="sm"
        >
            <DialogTitle id="alert-dialog-title">
                {isPublished ? 'Are you sure you want to publish the article?' : 'Are you sure you want to draft the article?'}
                <button onClick={(e) => setShowEditPopup(false)}><X /></button>
            </DialogTitle>
            <DialogContent>
                <DialogContentText id="alert-dialog-description">
                    <div className="ModalFormGroup">
                        <label className="PopupBodyText">{isPublished ? 'This article will be published immediately. You can draft it at any time.' : 'This article will be draft immediately. You can publish it at any time.'}</label>
                    </div>
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <button onClick={(e) => setShowEditPopup(false)} className="CancelPopupBU">Cancel</button>
                <button onClick={(e) => confirmStChange()} className="SubmitPopupBU">{isPublished ? 'Publish' : 'Draft'}</button>
            </DialogActions>
        </Dialog>

        <Dialog
            open={showDeletePopup}
            onClose={(e) => setShowDeletePopup(false)}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
            className="NewPopUp"
            fullWidth
            maxWidth="sm"
        >
            <DialogTitle id="alert-dialog-title">
                Are you sure you want to delete the article?
                <button onClick={(e) => setShowDeletePopup(false)}><X /></button>
            </DialogTitle>
            <DialogContent>
                <DialogContentText id="alert-dialog-description">
                    <div className="ModalFormGroup">
                        <label className="PopupBodyText">This article will be removed immediately. You can't undo this action.</label>
                    </div>
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <button className="CancelPopupBU" onClick={(e) => confirmDelete()}>Delete</button>
                <button onClick={(e) => setShowDeletePopup(false)} className="SubmitPopupBU">Cancel</button>
            </DialogActions>
        </Dialog>

        <Snackbar
            open={snackbarOpen}
            onClose={(e) => setSnackbarOpen(false)}
            TransitionComponent={Slide}
            message={snackbarMsg}
            autoHideDuration={10000}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            action={
                <IconButton
                    size="small"
                    aria-label="close"
                    color="inherit"
                    onClick={(e) => setSnackbarOpen(false)}
                >
                    <X />
                </IconButton>
            }
        />

        <Footer />
    </React.Fragment>);
};

export default withCustomAuthenticationRequired(ArticleList, {
    onRedirecting: () => <Loader />,
});
